

  
const content = `# OnData ODBC Driver

## Objective
The OnData Platform enables data encryption at-runtime, not just in-transit and at-rest, to better protect sensitive data. The protected data can stay encrypted while
being processed by database engines and is decrypted only when data access is required and authorized. With the OnData ODBC driver, data encryption and decryption
will be handled on the fly and are transparent to applications and users. The purpose of this guide is to detail the procedure to install and configure the OnData
ODBC Driver.

The OnData ODBC Driver can be configured to support various types of databases, such as Microsoft SQL, Oracle, PostgreSQL, Snowflake, DB2, MySQL, and MariaDB, running on both Windows and Linux
platforms. The target databases can be directly encrypted in customer's hosting environments using the OnData Encryption Tool, or encrypted using the OnData Portal. If the OnData 
Portal is used, the encrypted databases can either be deployed directly via the OnData Deployment services, or downloaded and restored in customer's hosting environments. Once the 
connectivity between your applications and the encrypted database is configured via the OnData ODBC Driver, the encrypted data read from the database will be automatically 
decrypted or masked by the OnData ODBC Driver based a user's data access roles. Also, for data to be stored to the database, if required to be encrypted, will be automatically encrypted before 
being stored to the database, so that all data that needs to be protected will stay encrypted in the database.  
  

## Windows Platform
The OnData ODBC Driver release package for Windows includes the following files:

* OnDataODBC.dll – The OnData ODBC Driver file;
* OnDataODBC.reg – The registry file for the OnData ODBC Driver;
* OnDataDriver.config – The configuration file for the OnData ODBC Driver;
* EncryptSQL.dll - The encryption module;
* Configurator.exe - The OnData Configurator tool for client registration;
* libcrypto-1_1-x64.dll – One of the three OpenSSL library files;
* libpq.dll – One of the three OpenSSL library files;
* libssl-1_1-x64.dll – One of the three OpenSSL library files;
* gdroot-g2.crt - The root certificate from the GoDaddy Class 2 Certification Authority.

Please follow the steps below to install and configure the OnData ODBC Driver on a computer where the driver will be used (this does not have to be a database server):  

* Copy all of the files from the OnData ODBC Driver release package to C:&bsol;Program Files&bsol;OnData&bsol;OnDataODBC&bsol;. Create such folders if they do not exist yet;

* Update the OnData ODBC Driver configuration file (OnDataDriver.config) with information about the database type and the associated database engine driver. The Database
  Type shall be consistent with the database engine to be used for running your database. The list of database engines supported include MSSQL, ORACLE, POSTGRESQL, SNOWFLAKE, DB2, MYSQL, and
  MARIADB for now. Populate the DriverFullPath entry with the location of the corresponding commercial database driver. The list of commercial database drivers is available from
  the [HKEY_LOCAL_MACHINE&bsol;SOFTWARE&bsol;ODBC&bsol;ODBCINST.INI&bsol;ODBC Drivers] registry key of your system. From there you can locate the specific commercial driver to use, and find
  the full path from the "Driver" entry in the [HKEY_LOCAL_MACHINE&bsol;SOFTWARE&bsol;ODBC&bsol;ODBCINST.INI&bsol;<DriverName>] registry key. Below is a sample configuration to use the Microsoft 
  SQL Server driver to connect to a SQL Server database:  

  \`\`\`bash
  DatabaseType = MSSQL
  DriverFullPath = C:\\WINDOWS\\system32\\SQLSRV32.dll
  \`\`\`

* Merge the registry file (OnDataODBC.reg) to the registry of the computer where the OnData ODBC Driver is installed. If the driver package is not located in the default path of 
C:&bsol;Program Files&bsol;OnData&bsol;OnDataODBC&bsol;, you may adjust the values of the "Driver" and "Setup" entries in the OnDataODBC.reg file accordingly to reflect the actual path before merging the registry file.  

* Configure your applications to connect to the encrypted database. For example, below is a sample connection string to connect to a Microsoft SQL Server database:  

  \`\`\`bash
  Driver=OnDataODBC;Server=AUSW8A01\\SQLEXPRESS01;Database=Northwind;Trusted_Connection=yes;
  \`\`\`
  
***Note:*** The system default GoDaddy root certificate will be used by default. If the ODBC driver cannot find such root certificate according to the driver log file, you need 
to copy the \`gdroot-g2.crt\` file to the path where the application that loads the ODBC driver is running from. You can find the running path from the driver log file as well.  


## Linux Platform
The OnData ODBC Driver release package for Linux includes the following folders and files:

* odbcinst.ini – Sample Linux ODBC driver configuration file with the OnData ODBC Driver included;
* OnDataODBC.so.<version_number> – The OnData ODBC Driver file;
* OnDataDriver.config – The configuration file for the OnData ODBC Driver.
* EncryptSQL.so.<version_number> - The encryption module;
* Configurator-<version_number> - The OnData Configurator tool for client registration;
* gdroot-g2.crt - The root certificate from the GoDaddy Class 2 Certification Authority.

Please follow the steps below to install and configure the OnData ODBC Driver on a computer where the driver will be used (this does not have to be a database server):  

* Install the OpenSSL library that the OnData ODBC Driver depends on from the command line (such as \`sudo apt-get install libssl\`);  
  
* Copy all files from the OnData ODBC Driver release package except the odbcinst.ini file to the Linux library folder or ODBC driver folder (such as \`/usr/lib64/\` on 
Oracle Linux, or \`/usr/lib/x86_64-linux-gnu/odbc/\` folder on Ubuntu);  

* Create soft links so that OnDataODBC.so is linked to OnDataODBC.so.<version_number> and EncryptSQL.so is linked to EncryptSQL.so.<version_number>;  

  \`\`\`bash
  ln -s /usr/lib/x86_64-linux-gnu/odbc/OnDataODBC.so.<version_number> /usr/lib/x86_64-linux-gnu/odbc/OnDataODBC.so  
  ln -s /usr/lib/x86_64-linux-gnu/odbc/EncryptSQL.so.<version_number> /usr/lib/x86_64-linux-gnu/odbc/EncryptSQL.so  
  \`\`\`
  
* Update the ODBC driver configuration file odbcinst.ini (under the \`/etc\` folder on Ubuntu) with a new [OnDataODBC] section as illustrated in the sample odbcinst.ini
  file of the release package:  

  \`\`\`bash
  [OnDataODBC]
  Description=OnData ODBC Driver
  Driver=/usr/lib/x86_64-linux-gnu/odbc/OnDataODBC.so
  \`\`\`

* Update the OnData ODBC Driver configuration file (OnDataDriver.config) with information about the data source and the associated database engine driver. The Database
  Type shall be consistent with the database engine to be used for running your database. The list of database engines supported include MSSQL, ORACLE, POSTGRESQL, SNOWFLAKE, DB2, MYSQL, 
  and MARIADB for now. Populate the DriverFullPath entry with the location of the corresponding commercial database driver. The location of the commercial database driver
  is available from the Driver entry in the odbcinst.ini file on your system. Below is a sample configuration to use the PostgreSQL driver to connect to a PostgreSQL database:  

  \`\`\`bash
  DatabaseType = POSTGRESQL
  DriverFullPath = /usr/lib/x86_64-linux-gnu/odbc/psqlodbcw.so
  \`\`\`

* Configure your applications to connect to the encrypted database. For example, below is a sample connection string to connect to a PostgreSQL database:  

  \`\`\`bash
  Driver=OnDataODBC;Server=localhost;Port=5432;Database=pagila;Uid=postgres;Pwd=xxxxxxxx;
  \`\`\`
  
***Note:*** The system default GoDaddy root certificate will be used by default. If the ODBC driver cannot find such root certificate according to the driver log file, you need 
to copy the \`gdroot-g2.crt\` file to either the \`/usr/share/certs\` folder or the path where the application that loads the ODBC driver is running from. You can find the running path 
from the driver log file as well.  
  
## Client Registration
Before the OnData ODBC driver can be used, it must be registered and activated as an OnData client. For each OnData ODBC driver, you can either run the OnData Configurator tool 
included in the OnData ODBC driver package to register it as a driver client, or create a driver client directly at the OnData Portal and populate the corresponding 
ClientId and ClientSecret information from the OnData Portal to the OnDataDriver.config file. For details on how to register a OnData driver client with the OnData Platform, 
please refer to the [**OnData Client Registration**](reference_doc#securedb/database_drivers/ondata_client_registration).  
  `
  
export default content;
